import {
  Component,
  HostListener,
  Inject,
  Injector,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { initializeApp } from 'firebase/app';
import { getAnalytics } from 'firebase/analytics';
import { environment } from '../../../environments/environment';
import { DomSanitizer, Meta, Title } from '@angular/platform-browser';
import {
  ActivatedRouteSnapshot,
  Data,
  IsActiveMatchOptions,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  Router,
} from '@angular/router';
import { StaticDataService } from '../../services/static-data.service';
import { TokenStorageService } from '../../services/token-storage.service';
import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { MatIconRegistry } from '@angular/material/icon';
import { AuthenticationService } from '../../services/authentication.service';
import { ConfigurationsService } from '../../services/configurations.service';
import { IApiResponse } from '../../model/response.moddel';
import { LoadSpinnerComponent } from '../../../../myt/utils/standalone-components/load-spinner/load-spinner.component';
import { SnackbarService } from '../../../../myt/utils/services/snackbar.service';
import { AnimationOptions } from 'ngx-lottie';
import { ContactDialogComponent } from '../../../../myt/modules/home/components/contact-dialog/contact-dialog.component';
import { UtilsService } from '../../services/utils.service';
import { ReminderWatcherService } from '../../services/reminder-watcher.service';
import { Subject, forkJoin, firstValueFrom, takeUntil } from 'rxjs';


interface IMatList {
  name: string;
  icon: string;
  active: boolean;
  id: string[];
  lines: number;
  secondaryText: string;
}

interface IToolbarMenu {
  name: string;
  icon: string;
  active: boolean;
  id: string[];
  lines: number;
  secondaryText: string;
  isExpendable: boolean;
  panelOpenState: boolean;
  expendableItems: IMatList[];
  isVisible: boolean;
}

@Component({
  selector: 'myt-root',
  templateUrl: './root.component.html',
  styleUrls: ['./root.component.scss'],
  standalone: false,
})
export class RootComponent implements OnInit, OnDestroy {
  // Initialize Firebase
  app = initializeApp(environment.firebaseConfig);
  readonly myMatchOptions: IsActiveMatchOptions = {
    queryParams: 'ignored',
    matrixParams: 'exact',
    paths: 'exact',
    fragment: 'exact',
  };
  analytics;
  viewId = 'other';
  servicesHeader = false;
  showIconOnly = true;
  showFloatingIcons = false;
  useFullWidthContent = false;
  hideFooter = false;
  isBrowser = false;
  /** Once true, we've already removed the #boot-splash-shell from the DOM. */
  private bootSplashDismissed = false;
  isHomeLanding = false;
  isToolbarScrolled = false;
  authToolbarReady = false;
  public matSpinner = LoadSpinnerComponent;
  isProduction = environment.production;
  isLoadingDebugEnabled = Boolean(environment.loadingDebug);
  whatsappOptions: AnimationOptions = {
    path: '/assets/lottie/whatsapp.json',
  };
  // Click-to-chat deep link for the floating right-side WhatsApp button.
  // Same pattern as `FooterComponent.whatsappCtaHref` — built once from
  // `environment.waNumber` + `environment.waPrefillMessage` so the
  // pre-filled message stays per-env-configurable.
  readonly whatsappCtaHref =
    `https://wa.me/${environment.waNumber}?text=${encodeURIComponent(environment.waPrefillMessage)}`;
  callOptions: AnimationOptions = {
    path: '/assets/lottie/call.json',
  };

  individualNavItems: IMatList[] = [
    {
      name: 'ITR Filing',
      icon: 'person',
      active: false,
      id: ['/packages', 'income-tax-return-filing'],
      lines: 1,
      secondaryText: 'Income Tax Return',
    },
    {
      name: 'Salary & Capital Gain',
      active: false,
      id: ['/packages', 'details', 'salary-plus-capital-gain'],
      lines: 1,
      secondaryText: '',
      icon: 'published_with_changes',
    },
    {
      name: 'Cryptocurrency',
      active: false,
      id: ['/packages', 'details', 'crypto-itr'],
      lines: 2,
      secondaryText: 'Including Salary',
      icon: 'description',
    },
    {
      name: 'Income Tax Notices',
      icon: 'person',
      active: false,
      id: ['/packages', 'itr-notice'],
      lines: 1,
      secondaryText: '',
    },
  ];

  businessNavItems: IMatList[] = [
    {
      name: 'File Tax',
      icon: 'person',
      active: false,
      id: ['/packages', 'business-tax-filing'],
      lines: 2,
      secondaryText: 'For Business / Profeesionals',
    },
    {
      name: 'GST Services',
      active: false,
      id: ['/packages', 'gst'],
      lines: 1,
      secondaryText: '',
      icon: 'lists',
    },

    {
      name: 'Startup Registration',
      active: false,
      id: ['/packages', 'details', 'startup'],
      lines: 1,
      secondaryText: '',
      icon: 'description',
    },
  ];

  resourcesNavItems: IMatList[] = [
    {
      name: 'Blog articles',
      icon: 'person',
      active: false,
      id: ['blog'],
      lines: 1,
      secondaryText: '',
    },
    {
      name: 'Tax Calculator',
      active: false,
      id: ['/calculator', 'tax-calculator'],
      lines: 1,
      secondaryText: '',
      icon: 'published_with_changes',
    },
  ];
  toolbarMenuList: IToolbarMenu[] = [
    {
      name: 'Dashboard',
      icon: 'grid_view',
      active: false,
      id: ['dashboard'],
      lines: 1,
      secondaryText: '',
      isExpendable: false,
      expendableItems: [],
      panelOpenState: false,
      isVisible: this.tokenStorageService.userProfile?.value?.userId
        ? true
        : false,
    },
    {
      name: 'Home',
      icon: 'home',
      active: false,
      id: ['/'],
      lines: 1,
      secondaryText: '',
      isExpendable: false,
      expendableItems: [],
      panelOpenState: false,
      isVisible: true,
    },
    {
      name: 'Blogs',
      icon: 'newspaper',
      active: false,
      id: ['/blogs'],
      lines: 1,
      secondaryText: '',
      isExpendable: false,
      expendableItems: [],
      panelOpenState: false,
      isVisible: true,
    },
    {
      name: 'Tax Calculator',
      icon: 'calculate',
      active: false,
      id: ['/calculators', 'tax-calculator'],
      lines: 1,
      secondaryText: '',
      isExpendable: false,
      expendableItems: [],
      panelOpenState: false,
      isVisible: true,
    },
    {
      name: 'Services',
      icon: 'settings_applications',
      active: false,
      id: [],
      lines: 1,
      secondaryText: '',
      isExpendable: true,
      panelOpenState: true,
      isVisible: true,
      expendableItems: [...this.individualNavItems, ...this.businessNavItems],
    },
    {
      name: 'About Us',
      icon: 'info',
      active: false,
      id: ['/about-us'],
      lines: 1,
      secondaryText: '',
      isExpendable: false,
      expendableItems: [],
      panelOpenState: false,
      isVisible: true,
    },
  ];
  activeIndexOfNavItems = 0;
  dataExtracted = false;
  private readonly destroy$ = new Subject<void>();
  private shouldRunWhatsappNotifications = false;
  private whatsappNotificationsStarted = false;
  private whatsappNotificationsStarting = false;
  private whatsappRealtimeFacade?: {
    connect(): void;
    disconnect(): void;
  };
  constructor(
    private matIconRegistry: MatIconRegistry,
    @Inject(DomSanitizer) private domSanitizer: DomSanitizer,
    protected tokenStorageService: TokenStorageService,
    private authService: AuthenticationService,
    @Inject(Meta) private meta: Meta,
    private router: Router,
    private staticData: StaticDataService,
    private configService: ConfigurationsService,
    private snackbar: SnackbarService,
    private dialog: MatDialog,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
    private utilsService: UtilsService,
    private reminderWatcher: ReminderWatcherService,
    private injector: Injector
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (this.isBrowser) {
      this.analytics = getAnalytics(this.app);
      this.addImagesToList();
    }
    router.events.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      if (val instanceof NavigationEnd) {
        this.updateRouteUiState(val.urlAfterRedirects);
      }
      // The first time *any* terminal navigation event lands — success,
      // cancel (e.g. NotAuthGuard redirected), or error — the router
      // has finished deciding where the user actually belongs. Remove
      // the index.html boot splash so the resolved route can render.
      // Lives in index.html (not Angular's template) so it covers
      // both the SSR-rendered route and the pre-hydration CSR window.
      if (
        !this.bootSplashDismissed &&
        this.isBrowser &&
        (val instanceof NavigationEnd ||
          val instanceof NavigationCancel ||
          val instanceof NavigationError)
      ) {
        this.bootSplashDismissed = true;
        const splash =
          this.document?.getElementById?.('boot-splash-shell');
        splash?.remove();
      }
    });
  }

  addImagesToList() {
    this.matIconRegistry.addSvgIcon(
      'whatsapp',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        'https://myt-website-public-data-prod.s3.ap-south-1.amazonaws.com/website assets/mind-your-tax/images/whatsapp.svg'
      )
    );
    this.matIconRegistry.addSvgIcon(
      'facebook',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        'https://myt-website-public-data-prod.s3.ap-south-1.amazonaws.com/website assets/mind-your-tax/images/facebook.svg'
      )
    );
    this.matIconRegistry.addSvgIcon(
      'pinterest',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        'https://myt-website-public-data-prod.s3.ap-south-1.amazonaws.com/website assets/mind-your-tax/images/pinterest.svg'
      )
    );
    this.matIconRegistry.addSvgIcon(
      'instagram',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        'https://myt-website-public-data-prod.s3.ap-south-1.amazonaws.com/website assets/mind-your-tax/images/instagram.svg'
      )
    );
    this.matIconRegistry.addSvgIcon(
      'linkedin',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        'https://myt-website-public-data-prod.s3.ap-south-1.amazonaws.com/website assets/mind-your-tax/images/linkedin.svg'
      )
    );
    this.matIconRegistry.addSvgIcon(
      'twitterx',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        'https://myt-website-public-data-prod.s3.ap-south-1.amazonaws.com/website assets/mind-your-tax/images/twitterx.svg'
      )
    );
  }

  ngOnInit(): void {
    this.updateRouteUiState(this.router.url);
    this.meta.addTags([
      {
        name: 'keywords',
        content: 'Income Tax Services, Income Tax Expert',
      },
      {
        name: 'description',
        content:
          'Wide Range of Income Tax Services, Tax Filing To Planning, Deductions, And Tax-Saving Strategies In Bangalore, India. File Income Tax Return Starts At Just ₹499',
      },
      { name: 'author', content: 'mindyourtax.in' },
      { name: 'robots', content: 'index, follow' },
    ]);
    let user = this.tokenStorageService.userProfile.value;

    if (!user?.userId) {
      // Try to hydrate from localStorage once
      const storedUser = this.tokenStorageService.loadUserFromLocalStorage();
      if (storedUser && storedUser.userId) {
        user = storedUser;
        this.tokenStorageService.userProfile.next(storedUser);
        this.authService.isAuthenticated = true;
      } else {
        // Anonymous visitor
        this.authService.isAuthenticated = false;
      }
    } else {
      // userProfile already had a valid user
      this.authService.isAuthenticated = true;
    }

    // Avoid hydrating the toolbar against a stale SSR auth state.
    this.authToolbarReady = this.isBrowser;

    this.updateToolbarVisibility();
    // This method already guards itself with `if (!this.dataExtracted) { ... }`
    this.dataToBeExtractedOnceAuthenticated();
    this.tokenStorageService.userProfile
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        const currentRole =
          user?.role ?? this.tokenStorageService.currentUserRole;

        if (user?.userId && currentRole !== 'CUSTOMER') {
          this.reminderWatcher.start();
          this.startWhatsappNotifications();
          return;
        }

        this.reminderWatcher.stop();
        this.stopWhatsappNotifications();
      });
  }

  private async startWhatsappNotifications(): Promise<void> {
    this.shouldRunWhatsappNotifications = true;

    if (
      this.whatsappNotificationsStarted ||
      this.whatsappNotificationsStarting
    ) {
      return;
    }

    this.whatsappNotificationsStarting = true;

    let facade: { connect(): void; disconnect(): void };
    try {
      const { WhatsappRealtimeFacade } = await import(
        '../../../../myt/modules/whatsapp/modules/messenger/services/whatsapp-realtime-facade.service'
      );
      facade = this.injector.get(WhatsappRealtimeFacade);
    } catch (error) {
      console.error('[RootComponent] failed to start WhatsApp notifications', error);
      this.whatsappNotificationsStarting = false;
      return;
    }

    this.whatsappNotificationsStarting = false;
    if (!this.shouldRunWhatsappNotifications) {
      return;
    }

    this.whatsappRealtimeFacade = facade;
    this.whatsappRealtimeFacade.connect();
    this.whatsappNotificationsStarted = true;
  }

  private stopWhatsappNotifications(): void {
    this.shouldRunWhatsappNotifications = false;

    if (!this.whatsappNotificationsStarted) {
      return;
    }

    this.whatsappRealtimeFacade?.disconnect();
    this.whatsappNotificationsStarted = false;
  }

  hasAuthenticatedUser(user: { userId?: number | null } | null | undefined): boolean {
    return !!user?.userId;
  }

  updateToolbarVisibility() {
    const isLoggedIn = this.hasAuthenticatedUser(this.tokenStorageService.userProfile.value);
    this.toolbarMenuList.find((i) => i.name === 'Dashboard')!.isVisible =
      isLoggedIn;
  }

  private updateRouteUiState(url: string): void {
    this.showFloatingIcons =
      !/^\/(?:agent-platform|agent|tickets|admin)(?:[\/?#]|$)/.test(url);

    if (url.includes('/tickets')) {
      this.servicesHeader = true;
      this.showIconOnly = false;
    } else if (url.includes('/thank-you')) {
      this.showIconOnly = true;
      this.servicesHeader = false;
    } else {
      this.servicesHeader = false;
      this.showIconOnly = false;
    }

    const routeData = this.getMergedRouteData(this.router.routerState.snapshot.root);
    this.useFullWidthContent = routeData['contentLayout'] === 'full-width';
    this.hideFooter = routeData['hideFooter'] === true;
    this.isHomeLanding =
      this.useFullWidthContent && /^\/(?:[?#].*)?$/.test(url);
    this.updateToolbarScrollState();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.updateToolbarScrollState();
  }

  private updateToolbarScrollState(): void {
    if (!this.isBrowser) {
      this.isToolbarScrolled = false;
      return;
    }

    this.isToolbarScrolled = window.scrollY > 20;
  }

  private getMergedRouteData(route: ActivatedRouteSnapshot | null): Data {
    let currentRoute = route;
    let mergedData: Data = {};

    while (currentRoute) {
      mergedData = { ...mergedData, ...currentRoute.data };
      currentRoute = currentRoute.firstChild ?? null;
    }

    return mergedData;
  }


async dataToBeExtractedOnceAuthenticated(): Promise<void> {
  if (this.dataExtracted) {
    return;
  }

  try {
    const combinedResponse = await firstValueFrom(
      forkJoin({
        categoryResponse: this.configService.getCategoryList(),
        subCategoryResponse: this.configService.getSubCategoryList(),
        documentClassificationResponse:
          this.configService.getDocumentClassificationList(),
      })
    );

    if (combinedResponse.categoryResponse?.status === 'Success') {
      this.configService.categoryList =
        combinedResponse.categoryResponse.data;
    }

    if (combinedResponse.subCategoryResponse?.status === 'Success') {
      this.configService.subCategoryList =
        combinedResponse.subCategoryResponse.data;
    }

    if (
      combinedResponse.documentClassificationResponse?.status === 'Success'
    ) {
      this.configService.documentClassificationList =
        combinedResponse.documentClassificationResponse.data;
    }

    this.dataExtracted = true;
    this.configService.isConfigDataAvailableSubject.next(true);
  } catch (error) {
    console.error('Error occurred while extracting config data', error);
  }
}


  openContactForm() {
    const dialogRef = this.dialog.open(ContactDialogComponent, {
      data: {
        category: this.viewId,
        subCategory: null,
      },
      width: '80vw',
    });
    dialogRef.afterClosed().subscribe((result) => {});
  }

  scrollIntoView(viewId: string, path: string[] = []) {
    if (this.router.url.length === 1) {
      if (this.isBrowser) {
        this.viewId = viewId;
        if (isPlatformBrowser(this.platformId) && this.document) {
          const element: HTMLElement = this.document.getElementById(
            viewId
          ) as HTMLElement;
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            element.classList.add('card-alternate');
            setTimeout(() => {
              element.classList.remove('card-alternate');
            }, 2000);
          }
        }
      }
    } else {
      this.router.navigate([...path, viewId]);
    }
  }

  checkIfLandingPage() {
    return this.router.url.length === 1;
  }

  setActiveNavItem(index: number, item: IMatList) {
    item.active = false;
    this.activeIndexOfNavItems = index;
  }

  signout() {
    if (environment.useBearerAuth) {
      this.tokenStorageService.signOut();
      this.authService.isAuthenticated = false;
      this.router.navigate(['/login']);
      return;
    }
    this.authService.signout().subscribe((response) => {
      if (response.status === 'Success') {
        this.snackbar.openSnackbar({
          message: `${response.message}`,
          type: 'success',
          duration: 5000,
        });
        this.tokenStorageService.signOut();
        this.authService.isAuthenticated = false;
        this.router.navigate(['/']);
      }
    });
  }

  goToDashboard(): void {
    this.utilsService.navigateToRoleDashboard();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.reminderWatcher.stop();
    this.stopWhatsappNotifications();
  }
}
